import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Dialog } from '@material-ui/core';
import { useAlgoliaProducts } from 'Hooks/useAlgoliaProducts';
import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import {
  Product as ProductDTO,
  ProductDetailsTabsIds,
  ProductPriceType
} from '@setvi/shared/interfaces';
import { Customer } from 'Services/Mongo/Types';
import { InvalidConfiguration } from 'pages/products/components/invalid-configuration';
import { ProductActionType } from 'pages/products/utils';
import { SQuadrantLayout } from '@setvi/shared/components';
import { ProductsList } from 'pages/products/components/products-list';
import { ProductDetails } from 'componentsV2/product/details';
import { Header } from 'pages/products/components/header';
import { useDialog } from '@setvi/shared/providers';
import { PromotionDialog } from 'pages/products/components/promotion';
import { useCatalogConfig } from 'pages/products/hooks/useCatalogConfig';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';
import { BottomLeftWrapper } from 'pages/products/components';
import { PricingType } from 'pages/products/api';
import { useProductStyles } from './styles';

export interface EmbeddedCatalogProps {
  selectedCustomer: Customer;
  selectedProducts: ProductDTO[];
  onProductSelect: (isChecked: boolean, selectedArray: ProductDTO[]) => void;
}

export const EmbeddedCatalog: FC<EmbeddedCatalogProps> = ({
  selectedCustomer,
  selectedProducts,
  onProductSelect
}) => {
  const [dialogOpened, setDialogOpened] = useState<boolean>(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDTO>();
  const [defaultTab, setDefaultOpenedTab] =
    useState<ProductDetailsTabsIds>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { companyData } = useAppContext();
  const userType = companyData.Settings[CompanySettingsTypeID.CompanyType]
    ?.Value as CompanyProductUserType;
  const { configuration, invalidConfiguration } = useCatalogConfig();

  const isFullPrice = useMemo(
    () => configuration?.price === ProductPriceType.Full,
    [configuration?.price]
  );

  const {
    facets,
    searchQuery,
    selectedFacets,
    selectedCategory,
    attributesForFaceting,
    isLoading: isSearchLoading,
    productPricesLoading,
    products: algoliaProducts,
    searchProducts,
    fetchMore: fetchMoreProducts,
    fetchProductsByCategory,
    onSelectFacet,
    handleGetAlgoliaProductPrice,
    handleUpdateAlgoliaProduct,
    refetchProducts
  } = useAlgoliaProducts({
    selectedCustomer,
    userType,
    canFetchPrices: isFullPrice,
    pricingType: PricingType.Quote
  });
  const { openDialog, closeDialog } = useDialog();
  const classes = useProductStyles();
  const { captureAnalytics } = useAnalytics();

  const handlePromotion = useCallback(
    (product: ProductDTO) => {
      openDialog(<PromotionDialog product={product} onClose={closeDialog} />);
    },
    [openDialog, closeDialog]
  );

  const openProductDetails = (
    product: ProductDTO,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => {
    setSelectedProduct(product);
    setDialogOpened(true);
    setDefaultOpenedTab(defaultOpenedTab);

    captureAnalytics(
      {
        productSKU: product?.sku,
        actionType: ProductActionType.DetailsView
      },
      AnalyticsModules.QuotesCatalog
    );
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => closeDialog(), []);

  if (invalidConfiguration) return <InvalidConfiguration />;

  return (
    <Box display="flex" height="100%" flex={1}>
      <SQuadrantLayout
        header={
          <Header
            searchQuery={{ value: searchQuery }}
            loading={isSearchLoading}
            selectedCategory={selectedCategory}
            handleSearch={val => {
              searchProducts(val.search.value);
            }}
            aiEnabled={false}
            onSelectCategory={fetchProductsByCategory}
            refetchProducts={refetchProducts}
          />
        }
        bottomLeft={
          <BottomLeftWrapper
            categoriesProps={{
              selectedCategory,
              onSelectCategory: ctgId => {
                fetchProductsByCategory(ctgId);
              }
            }}
            filtersProps={{
              globalSearch: searchQuery,
              onSelectFacet,
              selectedCategory,
              facets,
              loading: isSearchLoading,
              selectedFacets,
              attributesForFaceting
            }}
          />
        }
        bottomRight={
          <ProductsList
            products={algoliaProducts}
            config={{
              columns: configuration.product?.columns,
              grid: configuration.product?.grid,
              priceType: configuration?.price,
              stock: configuration?.product.grid?.stock,
              manufacturerSku: configuration?.product.grid?.manufacturerSku
            }}
            selectedCustomer={selectedCustomer}
            isLoading={isSearchLoading}
            disableSelect={false}
            productPricesLoading={productPricesLoading}
            searchQuery={searchQuery}
            selectedProducts={selectedProducts}
            updateProduct={handleUpdateAlgoliaProduct}
            drawerOpen={drawerOpen}
            canShowActionBar={false}
            fetchMore={fetchMoreProducts}
            onPromotionClick={handlePromotion}
            openProductDetails={openProductDetails}
            onGetProductPrice={product =>
              handleGetAlgoliaProductPrice({
                productList: product,
                accountNumber: selectedCustomer.accountNumber
              })
            }
            setDrawerOpen={setDrawerOpen}
            handleProductSelect={onProductSelect}
            composeEmailValues={undefined}
          />
        }
      />
      <Dialog
        maxWidth="md"
        aria-labelledby="simple-dialog-title"
        open={selectedProduct && dialogOpened}
        style={{ zIndex: '1300' }}
        onKeyDown={event => {
          if (event.key === 'Escape') setDialogOpened(false);
        }}
        classes={{ paper: classes.dialogPaper }}>
        <ProductDetails
          selectedProduct={selectedProduct}
          selectedProducts={selectedProducts}
          selectedCustomerId={selectedCustomer?._id}
          defaultOpenedTab={defaultTab}
          userType={userType}
          handleProductSelect={onProductSelect}
          canSelectProducts
          captureResourceAnalytics={() =>
            captureAnalytics(
              {
                productSKU: selectedProduct?.sku,
                actionType: ProductActionType.AssetView
              },
              AnalyticsModules.QuotesCatalog
            )
          }
          openProductDetails={openProductDetails}
          updateProduct={handleUpdateAlgoliaProduct}
          onClose={() => setDialogOpened(!dialogOpened)}
          handleSelected={() => setDrawerOpen(!drawerOpen)}
        />
      </Dialog>
    </Box>
  );
};
